import { PantallaCargando } from '@/components/Cargando';
import { useOutletContext } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { ShoppingBag, Plus, Image, Music, Video, Youtube, FileText, X, History } from 'lucide-react';

// S48 (9/N) — only these Knowledge Library asset types make sense to
// attach to a catalog item as shareable media (documents/FAQs/etc. stay
// out of this picker — they belong to the AI's text context, not to "here's
// a photo/video of this product").
const MEDIA_ASSET_TYPES = ['image_asset', 'video_asset', 'audio_asset', 'youtube_link'];
const MEDIA_TYPE_ICONS = { image_asset: Image, video_asset: Video, audio_asset: Music, youtube_link: Youtube };

const EMPTY_FORM = {
  item_type: '', name: '', description: '', price_min: '', specs: {}, brand_id: '',
  // Sprint 47 (1/N) — vertical detail fields, flat like the backend payload.
  duration_minutes: '', sessions_included: '', level: '', category: '',
  year: '', mileage_km: '', transmission: '', fuel_type: '', color: '', condition: '', vin: '',
  land_m2: '', construction_m2: '', bedrooms: '', bathrooms: '', parking_spots: '', property_condition: '',
  // Sprint 75 (2/N) — Desarrollos Inmobiliarios: ubicación de la unidad
  // (mapa de disponibilidad) + estado de venta real.
  development_id: '', tower: '', floor: '', unit_number: '', sales_status: 'available',
  // Sprint 56 (1/N) — membership family: descuento automático, créditos
  // prepagados, tier VIP, y si da prioridad de reservación.
  tier_label: '', discount_percent: '', discount_scope: 'all', discount_category: '',
  included_credits: '', priority_booking: false, validity_days: '',
  // Sprint 48 (1/N) — opt-in inventory, orthogonal to item_type family.
  track_inventory: false, quantity_available: '',
  // Sprint 48 (9/N) — image/video/audio/YouTube-link attachments, always
  // selected from the Knowledge Library (never uploaded from this form).
  media_asset_ids: [],
  // Sprint 57 (1/N) — un ítem puede asociarse a una, varias, o todas las
  // categorías del tenant (reemplaza la categoría única de texto libre
  // de Sprint 55 — ver CatalogCategory/CatalogItemCategory).
  category_ids: [],
  resource_ids: [],
};

// Sprint 47 (1/N) — same three families the backend groups item_types into
// (routers/business_hub.py::detail_family_for_item_type). Each type entry
// from /tenant/catalog/item-types already carries its own `family`.
const FAMILY_FIELDS = {
  service: [
    { key: 'duration_minutes', label: 'Duración (minutos)', required: true, type: 'number' },
    { key: 'sessions_included', label: 'Sesiones incluidas', type: 'number' },
    { key: 'level', label: 'Nivel', type: 'select', options: [['', 'Sin especificar'], ['principiante', 'Principiante'], ['intermedio', 'Intermedio'], ['avanzado', 'Avanzado']] },
    { key: 'category', label: 'Categoría', type: 'text' },
  ],
  vehicle: [
    { key: 'year', label: 'Año', required: true, type: 'number' },
    { key: 'mileage_km', label: 'Kilometraje', type: 'number' },
    { key: 'transmission', label: 'Transmisión', type: 'select', options: [['', 'Sin especificar'], ['manual', 'Manual'], ['automatica', 'Automática']] },
    { key: 'fuel_type', label: 'Combustible', type: 'text' },
    { key: 'color', label: 'Color', type: 'text' },
    { key: 'condition', label: 'Condición', type: 'select', options: [['', 'Sin especificar'], ['nuevo', 'Nuevo'], ['seminuevo', 'Seminuevo'], ['usado', 'Usado']] },
    { key: 'vin', label: 'VIN / Número de serie', type: 'text' },
  ],
  property: [
    { key: 'land_m2', label: 'm² de terreno', required: true, type: 'number' },
    { key: 'construction_m2', label: 'm² de construcción', type: 'number' },
    { key: 'bedrooms', label: 'Recámaras', type: 'number' },
    { key: 'bathrooms', label: 'Baños', type: 'number' },
    { key: 'parking_spots', label: 'Cajones de estacionamiento', type: 'number' },
    { key: 'property_condition', label: 'Condición', type: 'select', options: [['', 'Sin especificar'], ['nueva', 'Nueva'], ['usada', 'Usada'], ['en_construccion', 'En construcción']] },
    // Sprint 75 (2/N) — solo tienen sentido si la unidad pertenece a un
    // desarrollo (mapa de disponibilidad estilo Dealider).
    { key: 'tower', label: 'Torre / sección', type: 'text' },
    { key: 'floor', label: 'Piso', type: 'number' },
    { key: 'unit_number', label: 'Número de unidad', type: 'text' },
    { key: 'sales_status', label: 'Estado de venta', type: 'select', options: [['available', 'Disponible'], ['reserved', 'Apartado'], ['sold', 'Vendido'], ['blocked', 'Bloqueado']] },
  ],
  // Sprint 56 (1/N) — memberships have no required field: a bare shell
  // (just name + price) is valid, everything below is an optional benefit.
  membership: [
    { key: 'tier_label', label: 'Nombre del nivel (ej. VIP, Oro)', type: 'text' },
    { key: 'discount_percent', label: 'Descuento automático (%)', type: 'number' },
    { key: 'discount_scope', label: 'El descuento aplica a', type: 'select', options: [['all', 'Todo el catálogo'], ['category', 'Solo una categoría']] },
    { key: 'discount_category', label: 'Categoría (si elegiste "Solo una categoría")', type: 'text' },
    { key: 'included_credits', label: 'Créditos incluidos (ej. 10 cortes)', type: 'number' },
    { key: 'validity_days', label: 'Vigencia en días (vacío = sin vencimiento)', type: 'number' },
  ],
};

// S76 — Inventario: el backend guarda 4 tipos de movimiento
// (VALID_INVENTORY_MOVEMENT_TYPES en models.py) pero 'sale' solo lo escribe
// el Punto de Venta; desde esta pantalla nunca se envía.
const MOVEMENT_LABELS = {
  sale: 'Venta en Punto de Venta',
  restock: 'Entrada de mercancía',
  adjustment: 'Ajuste por conteo',
  return: 'Devolución',
};

// Cada opción arma el par (tipo de movimiento, signo) que espera el backend,
// para que el usuario nunca tenga que escribir un número negativo.
const ADJUST_OPTIONS = [
  { key: 'restock_in', label: 'Entró mercancía (compra o reabastecimiento)', movement_type: 'restock', sign: 1 },
  { key: 'return_in', label: 'Un cliente devolvió piezas (regresan al inventario)', movement_type: 'return', sign: 1 },
  { key: 'adjustment_in', label: 'Conteo físico: hay MÁS de lo registrado', movement_type: 'adjustment', sign: 1 },
  { key: 'adjustment_out', label: 'Conteo físico: hay MENOS (merma, daño o pérdida)', movement_type: 'adjustment', sign: -1 },
];

function itemDetailSummary(item) {
  if (item.detail_family === 'service' && item.duration_minutes) {
    return `${item.duration_minutes} min${item.level ? ` • ${item.level}` : ''}`;
  }
  if (item.detail_family === 'vehicle' && item.year) {
    return [item.year, item.mileage_km ? `${item.mileage_km.toLocaleString()} km` : null, item.transmission].filter(Boolean).join(' • ');
  }
  if (item.detail_family === 'property' && item.land_m2) {
    return [`${item.land_m2} m²`, item.bedrooms ? `${item.bedrooms} rec` : null, item.bathrooms ? `${item.bathrooms} baños` : null].filter(Boolean).join(' • ');
  }
  return null;
}

export default function CatalogHub() {
  const outlet = useOutletContext() || {};
  const isDeveloperCatalog = !!(outlet.isDeveloper || outlet.dict?.is_developer);

  const { user } = useAuth();
  // Mismo gate que el backend (_require_admin): solo dueño/administrador
  // puede borrar categorías o mover inventario. Los demás solo consultan.
  const canManageCatalog = ['tenant_owner', 'tenant_admin'].includes(user?.tenant_role);
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [brandForm, setBrandForm] = useState({ name: '', vehicle_kind: 'car' });
  const [brandSearch, setBrandSearch] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [recursos, setRecursos] = useState([]);
  const [newRec, setNewRec] = useState({ resource_type: 'room', name: '', user_id: '' });
  const [team, setTeam] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState('');
  const [mediaAssets, setMediaAssets] = useState([]); // Knowledge Library assets selectable as catalog media
  const [categories, setCategories] = useState([]); // Sprint 57 (1/N) — tenant's category taxonomy
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [developments, setDevelopments] = useState([]); // Sprint 75 (2/N) — Desarrollos Inmobiliarios
  const [deletingCategoryId, setDeletingCategoryId] = useState('');
  // S76 — Movimientos de inventario: se cargan al abrir el panel, nunca al montar.
  const [inventoryItem, setInventoryItem] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState('');
  const [adjustForm, setAdjustForm] = useState({ option: 'restock_in', quantity: '', notes: '' });
  const [savingAdjust, setSavingAdjust] = useState(false);

  // S81 — modal a document.body (evita overflow-hidden del TenantLayout).
  // Bloquea scroll del body y Escape cierra la ficha de edición.
  useEffect(() => {
    if (!showForm) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShowForm(false);
        setEditingId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [showForm]);

  const load = () => {
    Promise.all([
      api.get('/tenant/catalog/items').then(r => setItems(r.data || [])),
      api.get('/tenant/knowledge/assets?page_size=100').then(r =>
        setMediaAssets((r.data?.assets || []).filter(a => MEDIA_ASSET_TYPES.includes(a.asset_type) && a.status !== 'archived'))
      ).catch(() => {}),
      api.get('/tenant/catalog/item-types').then(r => setTypes(r.data || [])),
      api.get('/tenant/agenda/resources').then(r => setRecursos(r.data?.items || r.data?.resources || [])).catch(() => {}),
      api.get('/tenant/business/team').then(r => setTeam(r.data?.members || r.data?.items || (Array.isArray(r.data) ? r.data : []))).catch(() => {}),
      api.get('/tenant/catalog/brands').then(r => setBrands(r.data || [])).catch(() => {}),
      api.get('/tenant/catalog/categories').then(r => setCategories(r.data || [])).catch(() => {}),
      api.get('/tenant/catalog/developments').then(r => setDevelopments(r.data || [])).catch(() => {}),
    ]).catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const createCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    setCreatingCategory(true);
    try {
      const r = await api.post('/tenant/catalog/categories', { name });
      setCategories(prev => prev.some(c => c.id === r.data.id) ? prev : [...prev, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm(f => ({ ...f, category_ids: [...f.category_ids, r.data.id] }));
      setNewCategoryName('');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setCreatingCategory(false); }
  };

  // S76 — Borrar una categoría equivocada (antes solo se podían crear, así que
  // un nombre con error de dedo quedaba para siempre en el selector y en el
  // reporte de ventas por categoría del Punto de Venta).
  const deleteCategory = async (c) => {
    if (!window.confirm(`¿Eliminar la categoría "${c.name}"?\n\nLos productos que la tenían solo pierden esa etiqueta: no se borra ni se archiva ningún producto.`)) return;
    setDeletingCategoryId(c.id);
    try {
      await api.delete(`/tenant/catalog/categories/${c.id}`);
      setCategories(prev => prev.filter(x => x.id !== c.id));
      setForm(f => ({ ...f, category_ids: f.category_ids.filter(id => id !== c.id) }));
      setItems(prev => prev.map(it => ({ ...it, categories: (it.categories || []).filter(x => x.id !== c.id) })));
      toast.success('Categoría eliminada');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo eliminar la categoría');
    } finally {
      setDeletingCategoryId('');
    }
  };

  // S76 — Historial de inventario (carga perezosa: solo al abrir el panel).
  const loadInventory = async (itemId) => {
    setInventoryLoading(true);
    try {
      const r = await api.get(`/tenant/catalog/items/${itemId}/inventory`);
      setInventoryData(r.data || null);
      setInventoryError('');
    } catch (e) {
      setInventoryData(null);
      setInventoryError(formatApiError(e.response?.data?.detail) || 'No se pudo cargar el historial de inventario.');
    } finally {
      setInventoryLoading(false);
    }
  };

  const openInventory = (item) => {
    setInventoryItem(item);
    setInventoryData(null);
    setInventoryError('');
    setAdjustForm({ option: 'restock_in', quantity: '', notes: '' });
    loadInventory(item.id);
  };

  const closeInventory = () => {
    setInventoryItem(null);
    setInventoryData(null);
    setInventoryError('');
    setInventoryLoading(false);
  };

  const submitAdjust = async () => {
    if (!inventoryItem) return;
    const option = ADJUST_OPTIONS.find(o => o.key === adjustForm.option);
    const qty = Number(adjustForm.quantity);
    if (!option) return;
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Escribe cuántas piezas se movieron (un número mayor a cero).');
      return;
    }
    setSavingAdjust(true);
    try {
      const r = await api.post(`/tenant/catalog/items/${inventoryItem.id}/inventory/adjust`, {
        movement_type: option.movement_type,
        quantity_delta: Math.round(qty) * option.sign,
        notes: adjustForm.notes.trim() || null,
      });
      const newQty = r.data?.quantity_available;
      toast.success(`Inventario actualizado: ahora hay ${newQty} en existencia`);
      setItems(prev => prev.map(it => (it.id === inventoryItem.id ? { ...it, quantity_available: newQty } : it)));
      setInventoryItem(prev => (prev ? { ...prev, quantity_available: newQty } : prev));
      setAdjustForm(f => ({ ...f, quantity: '', notes: '' }));
      await loadInventory(inventoryItem.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo registrar el movimiento');
    } finally {
      setSavingAdjust(false);
    }
  };

  const selectedFamily = types.find(t => t.key === form.item_type)?.family || null;
  const familyFields = selectedFamily ? FAMILY_FIELDS[selectedFamily] : [];
  const requiredField = familyFields.find(f => f.required);
  const missingRequired = requiredField && !form[requiredField.key];

  const save = async () => {
    try {
      const numericFields = new Set([
        'duration_minutes', 'sessions_included', 'year', 'mileage_km',
        'land_m2', 'construction_m2', 'bedrooms', 'bathrooms', 'parking_spots', 'floor',
        'quantity_available', 'discount_percent', 'included_credits', 'validity_days',
      ]);
      const payload = { ...form, price_min: form.price_min ? parseFloat(form.price_min) : null, brand_id: form.brand_id || undefined };
      for (const key of numericFields) {
        payload[key] = form[key] === '' ? null : Number(form[key]);
      }
      // Sprint 53 (14/N) — antes esta pantalla solo podía CREAR: el
      // backend ya tenía PUT completo (incluyendo el mismo _sync_catalog_media
      // que create usa) pero nada en el frontend lo llamaba, así que un
      // producto ya creado nunca podía cambiar su multimedia/datos después.
      if (editingId) {
        await api.put(`/tenant/catalog/items/${editingId}`, payload);
        toast.success('Ítem actualizado');
      } else {
        await api.post('/tenant/catalog/items', payload);
        toast.success('Ítem creado');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (item) => {
    setForm({
      ...EMPTY_FORM, ...item,
      price_min: item.price_min != null ? String(item.price_min) : '',
      brand_id: item.brand_id || '',
      quantity_available: item.quantity_available != null ? String(item.quantity_available) : '',
      media_asset_ids: (item.media || []).map(m => m.asset_id),
      category_ids: (item.categories || []).map(c => c.id),
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const recargarRecursos = () => api.get('/tenant/agenda/resources').then(r => setRecursos(r.data?.items || [])).catch(() => {});
  const crearRecurso = async () => {
    if (!newRec.name.trim()) { toast.error('Ponle nombre al recurso'); return; }
    try {
      await api.post('/tenant/agenda/resources', {
        resource_type: newRec.resource_type, name: newRec.name.trim(),
        user_id: newRec.resource_type === 'staff' ? (newRec.user_id || undefined) : undefined,
      });
      setNewRec({ resource_type: 'room', name: '', user_id: '' });
      await recargarRecursos(); toast.success('Recurso creado');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleRecurso = async (r) => {
    try { await api.put(`/tenant/agenda/resources/${r.id}`, { is_active: !r.is_active }); await recargarRecursos(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const filtered = filter ? items.filter(i => i.item_type === filter) : items;
  // Ítem que se está editando, tal como está guardado hoy (para mostrar su
  // existencia real: el inventario nunca se edita desde el formulario).
  const editingItem = editingId ? items.find(i => i.id === editingId) : null;

  if (loading) return <PantallaCargando />;

  const isAutomotive = types.some(t => t.key.startsWith('vehicle') || t.key.startsWith('motorcycle'));

  return (
    <div className="space-y-4" data-testid="catalog-hub" data-developer={isDeveloperCatalog ? "1" : "0"}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#64748B]">Tu catálogo de productos y servicios</p>
        {filter !== '__recursos__' && (
          <Button onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }} className="bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="catalog-new-item-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo
          </Button>
        )}
      </div>

      
      {isDeveloperCatalog && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900" data-testid="developer-units-banner">
          <p className="font-semibold">Unidades por desarrollo</p>
          <p className="text-xs text-sky-800 mt-0.5">
            Como desarrollador, el inventario vive en cada <strong>Desarrollo</strong> (mapa torre → piso → unidad).
            Aquí ves el listado completo; para cargar unidades nuevas usa el mapa del desarrollo o el formulario eligiendo el desarrollo.
          </p>
        </div>
      )}
<SectionHelp
        module="catalog_verticalized"
        title="las fichas de catálogo"
        description="Según el tipo de ítem que elijas (servicio, producto, vehículo, propiedad...), el formulario te pide los datos que de verdad importan para ese tipo de negocio — duración de un servicio, año de un vehículo, metros de una propiedad. 'Producto' está disponible siempre, sin importar tu giro. Cada ítem puede asociarse a una, varias o todas tus categorías — créalas la primera vez que las necesites, sin salir de este formulario. Un dato marcado con * es obligatorio para poder guardar. Si un ítem lleva control de inventario, toca sus existencias en la lista para ver de dónde salió cada cambio y registrar entradas, devoluciones o ajustes por conteo."
        whatItDoesNot="No inventa ni completa datos por ti — si dejas un campo opcional vacío, simplemente no aparece en la ficha."
      />

      {/* Filters */}
      <div className="flex gap-1 overflow-x-auto">
        <button onClick={() => setFilter('')} className={`px-3 py-1.5 text-xs rounded-full ${!filter ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#64748B]'}`}>Todos</button>
        {types.map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)} className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap ${filter === t.key ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#64748B]'}`}>{t.label}</button>
        ))}
        {/* C+D — Recursos como una pestaña más, después de Producto. */}
        <button onClick={() => setFilter('__recursos__')} className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap ${filter === '__recursos__' ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#64748B]'}`} data-testid="catalog-tab-recursos">Recursos</button>
      </div>

      {/* Item list (oculta en la pestaña Recursos) */}
      {filter !== '__recursos__' && (filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center">
          <ShoppingBag className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
          <p className="text-sm text-[#64748B]">No hay ítems en tu catálogo</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(item => {
            const detailSummary = itemDetailSummary(item);
            return (
              <div key={item.id} className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex items-center gap-3" data-testid={`catalog-item-${item.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A]">{item.name}</p>
                  <p className="text-xs text-[#64748B]">{item.item_type_label}{item.price_min ? ` • $${item.price_min}` : ''}</p>
                  {detailSummary && <p className="text-xs text-[#94A3B8] mt-0.5">{detailSummary}</p>}
                  {item.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.categories.map(c => (
                        <span key={c.id} className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#F1F5F9] text-[#475569]">{c.name}</span>
                      ))}
                    </div>
                  )}
                  {item.track_inventory && (
                    <button type="button" onClick={() => openInventory(item)}
                      className="mt-0.5 inline-flex items-center gap-1 text-xs hover:opacity-75 transition-opacity"
                      title="Ver de dónde salió cada cambio de existencias y registrar entradas o ajustes"
                      data-testid={`catalog-item-inventory-${item.id}`}>
                      <span className={item.quantity_available > 0 ? 'text-[#059669]' : 'text-[#DC2626]'}>
                        {item.quantity_available > 0 ? `${item.quantity_available} en inventario` : 'Sin inventario disponible'}
                      </span>
                      <span className="text-[#64748B] underline decoration-dotted underline-offset-2 inline-flex items-center gap-0.5">
                        <History className="w-3 h-3" /> Movimientos
                      </span>
                    </button>
                  )}
                  {item.media?.length > 0 && (
                    <p className="text-xs text-[#94A3B8] mt-0.5 flex items-center gap-1">
                      <Image className="w-3 h-3" /> {item.media.length} archivo{item.media.length !== 1 ? 's' : ''} multimedia
                    </p>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => startEdit(item)} data-testid={`catalog-item-edit-${item.id}`}>Editar</Button>
              </div>
            );
          })}
        </div>
      ))}

      {/* C+D — Recursos como pestaña (después de Producto): cabinas/consultorios/
          salas/bahías y profesionales. Se asocian a cada servicio desde su ficha. */}
      {filter === '__recursos__' && (
      <div className="pt-2" data-testid="catalog-recursos-section">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-[#0F172A]">Recursos</h3>
        </div>
        <p className="text-[11px] text-[#94A3B8] mb-3">
          Los espacios (cabina, consultorio, sala, bahía), el equipo y los profesionales que prestan tus servicios.
          En cada servicio, arriba, eliges cuáles lo prestan. Una cabina o consultorio no necesita un usuario del sistema.
        </p>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div>
            <label className="text-[11px] text-[#64748B] block mb-1">Tipo</label>
            <select value={newRec.resource_type} onChange={e => setNewRec({ ...newRec, resource_type: e.target.value, user_id: '' })}
              className="px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="rec-type">
              <option value="room">Espacio (cabina/consultorio/sala/bahía)</option>
              <option value="staff">Profesional</option>
              <option value="equipment">Equipo</option>
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-[11px] text-[#64748B] block mb-1">Nombre</label>
            <input value={newRec.name} onChange={e => setNewRec({ ...newRec, name: e.target.value })}
              placeholder="Ej. Cabina 1, Consultorio 2, Bahía 3, Ana López"
              className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="rec-name" />
          </div>
          {newRec.resource_type === 'staff' && team.length > 0 && (
            <div>
              <label className="text-[11px] text-[#64748B] block mb-1">Usuario (opcional)</label>
              <select value={newRec.user_id} onChange={e => setNewRec({ ...newRec, user_id: e.target.value })}
                className="px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="rec-user">
                <option value="">Sin vincular</option>
                {team.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
          )}
          <Button size="sm" onClick={crearRecurso} className="bg-[#0F172A] text-white" data-testid="rec-add">
            <Plus className="w-4 h-4 mr-1" /> Agregar recurso
          </Button>
        </div>
        <div className="space-y-1.5">
          {recursos.filter(r => r.resource_type !== 'table').length === 0 && (
            <p className="text-xs text-[#94A3B8]">Aún no tienes recursos.</p>
          )}
          {recursos.filter(r => r.resource_type !== 'table').map(r => (
            <div key={r.id} className={`flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2 ${r.is_active ? '' : 'opacity-50'}`}>
              <div className="text-sm text-[#0F172A]">
                {r.name}
                <span className="text-[10px] text-[#94A3B8] ml-2">
                  {r.resource_type === 'staff' ? 'profesional' : r.resource_type === 'room' ? 'espacio' : 'equipo'}
                </span>
              </div>
              <Button size="sm" variant="outline" onClick={() => toggleRecurso(r)}>
                {r.is_active ? 'Desactivar' : 'Activar'}
              </Button>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Create form modal */}
      {showForm && createPortal(
        <div
          className="fixed inset-0 z-[200] overflow-y-auto overscroll-contain bg-slate-900/45"
          onClick={() => { setShowForm(false); setEditingId(null); }}
          data-testid="catalog-form-overlay"
          role="dialog"
          aria-modal="true"
        >
          {/* S81 — overlay scrolleable + panel anclado arriba en móvil.
              Antes: items-center + overflow del layout → solo se veía la sombra
              y la ficha quedaba fuera de vista (había que «bajar» sin poder). */}
          <div className="min-h-full flex items-start justify-center p-3 sm:p-6 sm:items-center">
          <div
            className="bg-white rounded-xl border border-[#E2E8F0] shadow-2xl w-full max-w-lg max-h-[min(92vh,880px)] flex flex-col my-2 sm:my-0"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-5 pt-4 pb-3 flex-shrink-0 border-b border-[#F1F5F9] bg-white rounded-t-xl">
              <h3 className="text-base font-bold text-[#0F172A]">
                {editingId ? 'Editar unidad / ítem' : 'Nuevo ítem de catálogo'}
              </h3>
              <button
                type="button"
                aria-label="Cerrar"
                data-testid="catalog-form-close"
                onClick={() => { setShowForm(false); setEditingId(null); }}
                className="text-[#94A3B8] hover:text-[#0F172A] p-1 rounded-md hover:bg-[#F1F5F9]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 px-5 py-3 overflow-y-auto flex-1 min-h-0 overscroll-contain">
              <select value={form.item_type} onChange={e => setForm(f => ({...EMPTY_FORM, item_type: e.target.value, name: f.name, description: f.description, price_min: f.price_min, track_inventory: f.track_inventory, quantity_available: f.quantity_available, media_asset_ids: f.media_asset_ids, category_ids: f.category_ids}))}
                className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="catalog-item-type-select">
                <option value="">Seleccionar tipo</option>
                {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" placeholder="Nombre" data-testid="catalog-item-name" />
              <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg resize-none" rows={2} placeholder="Descripción" />
              <input value={form.price_min} onChange={e => setForm(f => ({...f, price_min: e.target.value}))}
                className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" placeholder="Precio" type="number" />
              <div>
                <label className="text-xs text-[#64748B] mb-1 flex items-center gap-1">
                  Categorías (elige una, varias o todas)
                  {canManageCatalog && (
                    <HelpTooltip text="Toca el nombre para marcar o desmarcar la categoría en este producto. La «×» de la derecha borra la categoría de todo tu negocio (útil si la escribiste mal): los productos que la tenían solo pierden esa etiqueta, no se borra ningún producto." />
                  )}
                </label>
                {categories.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="catalog-category-picker">
                    {categories.map(c => {
                      const checked = form.category_ids.includes(c.id);
                      return (
                        <span key={c.id}
                          className={`inline-flex items-center rounded-full text-xs font-medium border transition-colors ${
                            checked ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8]'
                          }`}>
                          <button type="button"
                            onClick={() => setForm(f => ({
                              ...f,
                              category_ids: checked ? f.category_ids.filter(id => id !== c.id) : [...f.category_ids, c.id],
                            }))}
                            className={`px-2.5 py-1 ${canManageCatalog ? 'pr-1.5' : ''}`}
                            data-testid={`catalog-category-chip-${c.id}`}>
                            {c.name}
                          </button>
                          {canManageCatalog && (
                            <button type="button" onClick={() => deleteCategory(c)} disabled={deletingCategoryId === c.id}
                              title={`Eliminar la categoría ${c.name} de todo tu negocio`}
                              aria-label={`Eliminar la categoría ${c.name}`}
                              className={`pr-2 pl-0.5 py-1 disabled:opacity-40 ${checked ? 'text-white/70 hover:text-white' : 'text-[#94A3B8] hover:text-[#DC2626]'}`}
                              data-testid={`catalog-category-delete-${c.id}`}>
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      );
                    })}
                    {categories.length > 1 && (
                      <button type="button" onClick={() => setForm(f => ({ ...f, category_ids: categories.map(c => c.id) }))}
                        className="px-2.5 py-1 rounded-full text-xs font-medium border border-dashed border-[#94A3B8] text-[#64748B] hover:border-[#0F172A]">
                        Todas
                      </button>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  <input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createCategory(); } }}
                    placeholder="Ej. Bebidas, Cortes, Refacciones…"
                    className="flex-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="catalog-new-category-input" />
                  <Button type="button" variant="outline" size="sm" onClick={createCategory} disabled={creatingCategory || !newCategoryName.trim()} data-testid="catalog-new-category-btn">
                    + Nueva
                  </Button>
                </div>
              </div>

              {isAutomotive && form.item_type?.includes('vehicle') && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <select value={form.brand_id} onChange={e => setForm(f => ({...f, brand_id: e.target.value}))}
                      className="flex-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="catalog-brand-select">
                      <option value="">Seleccionar marca</option>
                      {brands.filter(b => brandSearch ? b.name.toLowerCase().includes(brandSearch.toLowerCase()) : true).filter(b => b.vehicle_kind === 'car').map(b => <option key={b.id} value={b.id}>{b.name}{b.is_system ? '' : ' (custom)'}</option>)}
                    </select>
                    <Button variant="outline" size="sm" onClick={() => setShowBrandModal(true)} data-testid="register-brand-btn">+ Marca</Button>
                  </div>
                  <input value={brandSearch} onChange={e => setBrandSearch(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-[#E2E8F0] rounded-lg" placeholder="Buscar marca..." />
                </div>
              )}

              {selectedFamily === 'property' && developments.length > 0 && (
                <div className="space-y-1 pt-1 border-t border-[#F1F5F9]">
                  <p className="text-xs font-medium text-[#334155] pt-2">Desarrollo (opcional)</p>
                  <select value={form.development_id} onChange={e => setForm(f => ({...f, development_id: e.target.value}))}
                    className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="catalog-development-select">
                    <option value="">Sin desarrollo (unidad independiente)</option>
                    {developments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <p className="text-[11px] text-[#94A3B8]">Si esta unidad pertenece a un desarrollo, aparecerá en su mapa de disponibilidad.</p>
                </div>
              )}

              {familyFields.length > 0 && (
                <div className="space-y-2 pt-1 border-t border-[#F1F5F9]">
                  <p className="text-xs font-medium text-[#334155] pt-2">
                    Datos de {selectedFamily === 'service' ? 'servicio' : selectedFamily === 'vehicle' ? 'vehículo' : selectedFamily === 'membership' ? 'membresía' : 'propiedad'}
                  </p>
                  {familyFields.map(field => (
                    <div key={field.key}>
                      <label className="text-xs text-[#64748B] mb-1 block">
                        {field.label}{field.required ? ' *' : ''}
                      </label>
                      {field.type === 'select' ? (
                        <select value={form[field.key]} onChange={e => setForm(f => ({...f, [field.key]: e.target.value}))}
                          className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid={`catalog-field-${field.key}`}>
                          {field.options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                        </select>
                      ) : (
                        <input value={form[field.key]} onChange={e => setForm(f => ({...f, [field.key]: e.target.value}))}
                          type={field.type} className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg"
                          data-testid={`catalog-field-${field.key}`} />
                      )}
                    </div>
                  ))}
                  {selectedFamily === 'membership' && (
                    <label className="flex items-center gap-2 cursor-pointer pt-1">
                      <input type="checkbox" checked={form.priority_booking}
                        onChange={e => setForm(f => ({...f, priority_booking: e.target.checked}))}
                        className="rounded border-[#E2E8F0]" data-testid="catalog-field-priority_booking" />
                      <span className="text-xs text-[#334155]">Da prioridad de reservación (VIP)</span>
                    </label>
                  )}
                </div>
              )}

              {['service', 'treatment', 'class', 'program', 'package'].includes(form.item_type) && (
                <div className="space-y-2 pt-2 border-t border-[#F1F5F9]">
                  <p className="text-xs font-medium text-[#334155] pt-2">Agenda: cómo se reserva</p>
                  <p className="text-[11px] text-[#94A3B8]">Duración y qué recursos lo prestan (cabina, consultorio, sala, bahía, profesional). Con esto ya se puede agendar sin configurar nada más.</p>
                  {!familyFields.some(f => f.key === 'duration_minutes') && (
                    <div>
                      <label className="text-xs text-[#64748B] mb-1 block">Duración (minutos)</label>
                      <input type="number" value={form.duration_minutes}
                        onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="catalog-field-duration_agenda" />
                    </div>
                  )}
                  <label className="text-xs text-[#64748B] block">Recursos que lo prestan</label>
                  {recursos.length === 0 ? (
                    <p className="text-xs text-[#94A3B8]">Aún no tienes recursos (cabinas/consultorios/profesionales). Créalos en la sección Recursos, abajo.</p>
                  ) : (
                    <div className="border border-[#E2E8F0] rounded-lg max-h-36 overflow-y-auto divide-y divide-[#F1F5F9]" data-testid="catalog-recursos">
                      {recursos.map(r => {
                        const checked = (form.resource_ids || []).includes(r.id);
                        return (
                          <label key={r.id} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-[#F8FAFC]">
                            <input type="checkbox" checked={checked} className="w-3.5 h-3.5"
                              onChange={e => setForm(f => ({
                                ...f,
                                resource_ids: e.target.checked
                                  ? [...(f.resource_ids || []), r.id]
                                  : (f.resource_ids || []).filter(id => id !== r.id),
                              }))} />
                            <span className="truncate">{r.name}</span>
                            <span className="text-[10px] text-[#94A3B8] ml-auto">
                              {r.resource_type === 'staff' ? 'profesional' : r.resource_type === 'room' ? 'espacio' : r.resource_type === 'equipment' ? 'equipo' : r.resource_type}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2 pt-2 border-t border-[#F1F5F9]">
                <p className="text-xs font-medium text-[#334155] pt-2">Multimedia (fotos, videos, audio, YouTube)</p>
                <p className="text-[11px] text-[#94A3B8]">
                  Se seleccionan de tu Biblioteca de la IA — súbelos ahí primero si no aparecen en esta lista.
                </p>
                {mediaAssets.length === 0 ? (
                  <p className="text-xs text-[#94A3B8]">Aún no tienes fotos, videos, audio ni links en tu Biblioteca.</p>
                ) : (
                  <div className="border border-[#E2E8F0] rounded-lg max-h-36 overflow-y-auto divide-y divide-[#F1F5F9]">
                    {mediaAssets.map(a => {
                      const Icon = MEDIA_TYPE_ICONS[a.asset_type] || FileText;
                      const checked = form.media_asset_ids.includes(a.id);
                      return (
                        <label key={a.id} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-[#F8FAFC]">
                          <input type="checkbox" checked={checked} className="w-3.5 h-3.5"
                            onChange={e => setForm(f => ({
                              ...f,
                              media_asset_ids: e.target.checked
                                ? [...f.media_asset_ids, a.id]
                                : f.media_asset_ids.filter(id => id !== a.id),
                            }))} />
                          <Icon className="w-3.5 h-3.5 text-[#64748B] flex-shrink-0" />
                          <span className="truncate">{a.title}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-[#F1F5F9]">
                <input type="checkbox" id="track-inventory" checked={form.track_inventory}
                  onChange={e => setForm(f => ({...f, track_inventory: e.target.checked}))}
                  className="w-4 h-4" data-testid="catalog-track-inventory" />
                <label htmlFor="track-inventory" className="text-xs text-[#334155]">Llevar control de inventario para este ítem</label>
              </div>
              {/* S76 — En modo edición ya NO se muestra "Cantidad inicial": el
                  backend ignora ese dato al actualizar (solo lo usa al crear),
                  así que antes el usuario creía estar cambiando la existencia y
                  no pasaba nada. Ahora se ve la existencia real y se cambia
                  desde el panel de movimientos, que deja registro de cada
                  entrada, devolución o ajuste. */}
              {form.track_inventory && (editingId ? (
                <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-3 space-y-2">
                  <p className="text-xs text-[#334155] flex items-center gap-1">
                    En existencia hoy: <strong>{editingItem?.quantity_available ?? 0}</strong>
                    <HelpTooltip text="La existencia no se escribe a mano: cambia con las entradas de mercancía, las devoluciones, los ajustes por conteo físico y las ventas del Punto de Venta. Así siempre puedes ver de dónde salió cada cambio." />
                  </p>
                  {editingItem?.track_inventory ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => openInventory(editingItem)} data-testid="catalog-open-inventory-from-form">
                      <History className="w-3.5 h-3.5 mr-1" /> Ver movimientos y ajustar
                    </Button>
                  ) : (
                    <p className="text-[11px] text-[#94A3B8]">Guarda primero este ítem para empezar a registrar movimientos de inventario.</p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="text-xs text-[#64748B] mb-1 flex items-center gap-1">
                    Cantidad inicial disponible
                    <HelpTooltip text="Cuántas piezas tienes hoy. Después de guardar, la existencia se mueve desde el panel de movimientos (entradas, devoluciones y ajustes por conteo) y con las ventas del Punto de Venta." />
                  </label>
                  <input value={form.quantity_available} onChange={e => setForm(f => ({...f, quantity_available: e.target.value}))}
                    type="number" min="0" className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg"
                    placeholder="0" data-testid="catalog-quantity-available" />
                </div>
              ))}
            </div>
            <div className="flex gap-2 px-5 py-4 flex-shrink-0 border-t border-[#F1F5F9] bg-white rounded-b-xl">
              <Button onClick={save} disabled={!form.item_type || !form.name || missingRequired} className="bg-[#0F172A] text-white flex-1" data-testid="catalog-save-btn">{editingId ? 'Guardar cambios' : 'Guardar'}</Button>
              <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancelar</Button>
            </div>
          </div>
          </div>
        </div>,
        document.body,
      )}
      {/* Brand registration modal */}
      {showBrandModal && (
        <div className="fixed inset-0 z-[210] overflow-y-auto bg-slate-900/50 p-4 flex items-start sm:items-center justify-center" onClick={() => setShowBrandModal(false)}>
          <div className="bg-white rounded-xl border shadow-xl max-w-sm w-full mx-4 p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-[#0F172A] mb-3">Registrar marca nueva</h3>
            <div className="space-y-3">
              <input value={brandForm.name} onChange={e => setBrandForm(f => ({...f, name: e.target.value}))}
                className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" placeholder="Nombre de la marca" data-testid="brand-name-input" />
              <select value={brandForm.vehicle_kind} onChange={e => setBrandForm(f => ({...f, vehicle_kind: e.target.value}))}
                className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg">
                <option value="car">Auto</option>
                <option value="motorcycle">Motocicleta</option>
              </select>
              <Button onClick={async () => {
                try {
                  await api.post('/tenant/catalog/brands', brandForm);
                  toast.success('Marca registrada');
                  setShowBrandModal(false);
                  setBrandForm({ name: '', vehicle_kind: 'car' });
                  api.get('/tenant/catalog/brands').then(r => setBrands(r.data || []));
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }} disabled={!brandForm.name} className="bg-[#0F172A] text-white w-full" data-testid="save-brand-btn">Registrar</Button>
            </div>
          </div>
        </div>
      )}

      {/* S76 — Movimientos de inventario + registro de entradas/ajustes.
          Se abre encima del formulario (z mayor) para no perder lo que el
          usuario esté editando. Los datos se piden al abrir, nunca al cargar
          la pantalla. */}
      {inventoryItem && (
        <div className="fixed inset-0 z-[210] overflow-y-auto bg-slate-900/50 p-4 flex items-start sm:items-center justify-center" onClick={closeInventory}>
          <div className="bg-white rounded-xl border shadow-xl max-w-md w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 pt-5 pb-3 flex-shrink-0 border-b border-[#F1F5F9]">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-[#0F172A] truncate">Inventario de {inventoryItem.name}</h3>
                  <p className="text-xs text-[#64748B] mt-0.5">
                    En existencia: <strong className={(inventoryData?.quantity_available ?? inventoryItem.quantity_available) > 0 ? 'text-[#059669]' : 'text-[#DC2626]'}>
                      {inventoryData?.quantity_available ?? inventoryItem.quantity_available ?? 0}
                    </strong>
                  </p>
                </div>
                <button type="button" onClick={closeInventory} aria-label="Cerrar"
                  className="text-[#94A3B8] hover:text-[#0F172A] flex-shrink-0" data-testid="catalog-inventory-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="px-5 py-3 overflow-y-auto flex-1 min-h-0 space-y-4">
              {/* Registrar movimiento — solo dueño/administrador (igual que el backend) */}
              {canManageCatalog ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-[#334155] flex items-center gap-1">
                    Registrar un movimiento
                    <HelpTooltip text="Elige qué pasó y cuántas piezas. El sistema suma o resta solo: nunca escribes números negativos. Las ventas del Punto de Venta se descuentan automáticamente, no las registres aquí." />
                  </p>
                  <select value={adjustForm.option} onChange={e => setAdjustForm(f => ({ ...f, option: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="catalog-inventory-movement-type">
                    {ADJUST_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                  <input value={adjustForm.quantity} onChange={e => setAdjustForm(f => ({ ...f, quantity: e.target.value }))}
                    type="number" min="1" step="1" placeholder="¿Cuántas piezas?"
                    className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="catalog-inventory-quantity" />
                  <input value={adjustForm.notes} onChange={e => setAdjustForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Nota opcional (ej. factura 1234, proveedor García)"
                    className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="catalog-inventory-notes" />
                  <Button type="button" onClick={submitAdjust}
                    disabled={savingAdjust || !adjustForm.quantity}
                    className="bg-[#0F172A] text-white w-full" data-testid="catalog-inventory-save-btn">
                    {savingAdjust ? 'Guardando…' : 'Registrar movimiento'}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-[#94A3B8]">Solo el dueño o un administrador puede registrar entradas y ajustes de inventario. Tú puedes consultar el historial.</p>
              )}

              {/* Historial */}
              <div className="space-y-2 pt-1 border-t border-[#F1F5F9]">
                <p className="text-xs font-medium text-[#334155] pt-2">Últimos movimientos</p>
                {inventoryLoading ? (
                  <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" /></div>
                ) : inventoryError ? (
                  <div className="space-y-2">
                    <p className="text-xs text-[#DC2626]">{inventoryError}</p>
                    <Button type="button" variant="outline" size="sm" onClick={() => loadInventory(inventoryItem.id)} data-testid="catalog-inventory-retry-btn">Reintentar</Button>
                  </div>
                ) : !inventoryData?.movements?.length ? (
                  <p className="text-xs text-[#94A3B8]">Sin movimientos todavía.</p>
                ) : (
                  <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#F1F5F9]" data-testid="catalog-inventory-movements">
                    {inventoryData.movements.map(m => (
                      <div key={m.id} className="px-3 py-2 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-[#0F172A]">{MOVEMENT_LABELS[m.movement_type] || m.movement_type}</p>
                          <p className="text-[11px] text-[#94A3B8]">
                            {m.created_at ? new Date(m.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : 'Sin fecha'}
                          </p>
                          {m.notes && <p className="text-[11px] text-[#64748B] mt-0.5 break-words">{m.notes}</p>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-xs font-semibold ${m.quantity_delta >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
                            {m.quantity_delta >= 0 ? `+${m.quantity_delta}` : m.quantity_delta}
                          </p>
                          <p className="text-[11px] text-[#94A3B8]">quedaron {m.quantity_after}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-[#94A3B8]">Se muestran los 50 movimientos más recientes, del más nuevo al más antiguo.</p>
              </div>
            </div>

            <div className="px-5 py-4 flex-shrink-0 border-t border-[#F1F5F9]">
              <Button variant="outline" onClick={closeInventory} className="w-full" data-testid="catalog-inventory-close-btn">Cerrar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
